import { Button } from '@wot/components';
import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { CgList } from 'react-icons/cg';
import { FaChalkboardTeacher } from 'react-icons/fa';
import { FiSearch } from 'react-icons/fi';
import { HiHome } from 'react-icons/hi';
import { MdOutlineSubject } from 'react-icons/md';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { BACK_ARROW, HAMBURGER } from '../assets/icons';
// import { NAV_LOGO } from '../assets/logos';
import NAV_LOGO from '../assets/logos/World_O_Tutor.webp';
// World_O_Tutor.png
import BreadCrumb from './BreadCrumb';
import { ChevronDown, Code2Icon } from 'lucide-react';
// import { SubjectDTO, subjectsData } from '../data/subjects';
import xhr from '../utils/fetch';

export default function Nav({
  sidebarOpen,
  onOpenSidebar,
  onCloseSidebar
}: {
  sidebarOpen: any;
  onOpenSidebar: any;
  onCloseSidebar: any;
}) {
  const [nav, setNav] = useState('home');
  const { id } = useParams();
  const navigate = useNavigate();
  const [path, setPath] = useState('');
  const [isHovered, setIsHovered] = useState(false);
  const [subjectData, setSubjectData] = useState(null);
  const paths = ['/find-tutor', `/find-tutor/${id}`, '/post-requirement', '/requirements'];

  // get all subject
  useEffect(() => {
    xhr({
      url: `/s/getAllCategories`,
      method: 'GET'
    })
      .then((res) => setSubjectData(res.data))
      .catch((err) => err);
  }, []);

  useEffect(() => {
    setPath(location.pathname);
  }, [location.pathname]);

  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (sidebarOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;

      // Prevent body scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        // Restore body scroll and position
        const scrollY = document.body.style.top;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      };
    }
  }, [sidebarOpen]);

  const [showHeader, setShowHeader] = useState({ show: true, scrollPos: 0 });
  const headerRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (window.scrollY < 100) {
      setShowHeader(() => ({ ...showHeader, show: true }));
    } else {
      setShowHeader((prev) => ({
        scrollPos: document.body.getBoundingClientRect().top,
        show: document.body.getBoundingClientRect().top > prev.scrollPos
      }));
    }
  };

  useLayoutEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  });

  if (paths.includes(path) && window.innerWidth < 768) {
    return (
      <div className="w-full h-14 md:h-16 flex items-center md:justify-around bg-white drop-shadow-md z-10 border-b-2 border-neutralBlue2 px-4">
        <div
          onClick={() => navigate(-1)}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-neutralBlue2 p-2 cursor-pointer"
        >
          <img src={BACK_ARROW} className="" />
        </div>
        <div className="px-4">
          <BreadCrumb />
        </div>
      </div>
    );
  }

  if (sidebarOpen) {
    return (
      <>
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-white bg-opacity-100 z-40 lg:hidden overflow-hidden scroll-none "
          onClick={() => {
            setIsHovered(false);
            onCloseSidebar();
          }}
        />

        {/* Sidebar */}
        <div className="absolute right-0 top-0 h-full w-80 max-w-[85vw]  shadow-2xl overflow-y-auto z-[999] lg:hidden">
          <div className="flex flex-col h-full ">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <Link to="/" onClick={() => onCloseSidebar()} className="flex-shrink-0">
                <img src={NAV_LOGO} alt="WOT logo" className="h-10 w-auto" />
              </Link>
              <button
                onClick={() => {
                  setIsHovered(false);
                  onCloseSidebar();
                }}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Close menu"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Navigation Links */}
            <div className="flex-1 px-6 py-6 space-y-2">
              <Link
                to="/"
                onClick={() => {
                  setIsHovered(false);
                  onCloseSidebar();
                }}
                className="flex items-center gap-4 px-4 py-3 rounded-xl hover:bg-blue-50 transition-all duration-200 group"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                  <HiHome className="w-5 h-5 text-blue-600" />
                </div>
                <span className="font-semibold text-gray-800 group-hover:text-blue-600">Home</span>
              </Link>

              <Link
                to={`${location.pathname === '/' ? '/find-tutor' : '/find-tutor'}`}
                onClick={() => {
                  setIsHovered(false);
                  onCloseSidebar();
                }}
                className="flex items-center gap-4 px-4 py-3 rounded-xl hover:bg-blue-50 transition-all duration-200 group"
              >
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center group-hover:bg-green-200 transition-colors">
                  <FiSearch className="w-5 h-5 text-green-600" />
                </div>
                <span className="font-semibold text-gray-800 group-hover:text-green-600">Find a Tutor</span>
              </Link>

              <Link
                to="/explore"
                onClick={() => {
                  setIsHovered(false);
                  onCloseSidebar();
                }}
                className="flex items-center gap-4 px-4 py-3 rounded-xl hover:bg-purple-50 transition-all duration-200 group"
              >
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                  <CgList className="w-5 h-5 text-purple-600" />
                </div>
                <span className="font-semibold text-gray-800 group-hover:text-purple-600">Explore</span>
              </Link>

              <Link
                to={process.env.REACT_APP_TUTOR_DOMAIN || '#'}
                onClick={() => {
                  setIsHovered(false);
                  onCloseSidebar();
                }}
                className="flex items-center gap-4 px-4 py-3 rounded-xl hover:bg-orange-50 transition-all duration-200 group"
              >
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                  <FaChalkboardTeacher className="w-5 h-5 text-orange-600" />
                </div>
                <span className="font-semibold text-gray-800 group-hover:text-orange-600">Become a Tutor</span>
              </Link>

              {/* Curriculum Dropdown */}
              <div className="relative">
                <button
                  className={`w-full flex items-center gap-4 px-4 py-3 rounded-xl transition-all duration-200 ${
                    isHovered ? 'bg-indigo-50' : 'hover:bg-indigo-50'
                  } group`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsHovered(!isHovered);
                  }}
                  aria-expanded={isHovered}
                  aria-controls="curriculum-dropdown"
                >
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                      isHovered ? 'bg-indigo-200' : 'bg-indigo-100 group-hover:bg-indigo-200'
                    }`}
                  >
                    <MdOutlineSubject
                      className={`w-5 h-5 transition-colors ${isHovered ? 'text-indigo-700' : 'text-indigo-600'}`}
                    />
                  </div>
                  <div className="flex items-center justify-between flex-1">
                    <span
                      className={`font-semibold transition-colors ${
                        isHovered ? 'text-indigo-700' : 'text-gray-800 group-hover:text-indigo-600'
                      }`}
                    >
                      Curriculum
                    </span>
                    <ChevronDown
                      className={`w-5 h-5 transition-transform duration-200 ${
                        isHovered ? 'rotate-180 text-indigo-600' : 'text-gray-500'
                      }`}
                    />
                  </div>
                </button>

                {/* Curriculum Dropdown Content */}
                {isHovered && (
                  <div
                    id="curriculum-dropdown"
                    className="absolute top-full left-4 right-4 z-[1001] mt-2 bg-white border border-gray-200 rounded-xl shadow-xl"
                  >
                    <div className="p-4 max-h-80 overflow-y-auto">
                      <NavItem subjectCategory={subjectData} setIsHovered={setIsHovered} />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="px-6 py-6 border-t border-gray-100 space-y-3 flex flex-col justify-center items-center">
              {/* <Link 
                to={process.env.REACT_APP_TUTOR_DOMAIN || '#'} 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={() => {
                  setIsHovered(false);
                  onCloseSidebar();
                }}
                className="block w-full text-center px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                Become a Tutor
              </Link> */}

              <Link
                to="/booktrail"
                onClick={() => {
                  setIsHovered(false);
                  onCloseSidebar();
                }}
                className="flex w-full justify-center"
              >
                <Button text="Book a Free Trial" onClick={() => {}} />
              </Link>

              <div className="grid grid-cols-2 gap-3">
                {/* <Link 
                  to="https://worldotutor.schoolpad.in/loginManager/load" 
                  onClick={() => {
                    setIsHovered(false);
                    onCloseSidebar();
                  }}
                  className="block"
                >
                  <Button text="ERP Login" onClick={() => {}} />
                </Link> */}

                <div className="relative  group">
                  <Button text="LOGIN" onClick={() => {}} />
                  {/* Dropdown for login/register */}
                  <div className="absolute left-0 mt-2 w-40  z-50 hidden group-hover:block bg-transparent">
                    <div className=" bg-white shadow-lg border w-fite border-gray-200  p-2 relative">
                      <div className="flex flex-col gap-0">
                        {/* <Link
                          to="https://worldotutor.schoolpad.in/loginManager/load"
                          className="block px-4 py-2 text-primary text-base font-semibold hover:bg-primary hover:text-white transition-all"
                        >
                          ERP Login
                        </Link> */}
                        <Link
                          to="https://learn.worldotutor.com/login"
                          className="block px-4 py-2 text-primary text-base font-semibold hover:bg-primary hover:text-white transition-all"
                        >
                          Teacher Login
                        </Link>

                        <Link
                          to="https://learn.worldotutor.com/login"
                          className="block px-4 py-2 text-primary text-base font-semibold hover:bg-primary hover:text-white transition-all"
                        >
                          Student Login
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>

                <Link
                  to="/paynow"
                  onClick={() => {
                    setIsHovered(false);
                    onCloseSidebar();
                  }}
                  className="block"
                >
                  <Button text="Pay Now" onClick={() => {}} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <nav
      ref={headerRef}
      className={`fixed ${
        showHeader.show ? 'top-0' : 'top-0'
      } transition-all w-full  mx-auto bg-white drop-shadow-md z-50`}
    >
      <div className="relative container h-14 md:h-16 mx-auto flex items-center gap-3">
        <div className="flex gap-4 items-center lg:justify-center cursor-pointer">
          {/* <div className="block lg:hidden w-10 h-6">
            <img onClick={() => onOpenSidebar()} src={HAMBURGER} className="w-6 h-6 cursor-pointer" />
          </div> */}
          <Link to="/" className="h-8 m:w-full w-40 pt-2 lg:pt-0">
            {/* <img src={NAV_LOGO} alt="WOT logo" className="h-10 md:w-full" /> */}
            <img src={NAV_LOGO} alt="WOT logo" className="h-8  md:w-full" />
          </Link>
        </div>

        <div className="w-full flex items-center  justify-end gap-2">
          <div className="lg:flex lg:justify-center hidden" id="navbar-default">
            <ul className="flex items-center gap-6 lg:py-4 w-fit">
              <li onClick={() => setNav('home')}>
                <Link to="/" className={`text-base font-medium  hover:text-primary ${nav === 'home' ? '' : ''} `}>
                  Home
                </Link>
              </li>
              {/* <li onClick={() => setNav('about')}>
                <Link
                  to="/about"
                  className={`text-base font-medium  hover:text-primary ${nav === 'about' ? '' : ''} `}
                >
                  About Us
                </Link>
              </li> */}
              <li onClick={() => setNav('find-tutor')}>
                <Link
                  to={`${location.pathname === '/' ? '/find-tutor' : '/find-tutor'}`}
                  className={`text-base text-nowrap font-medium  hover:text-primary ${nav === 'find-tutor' ? '' : ''} `}
                >
                  Find a Tutor
                </Link>
              </li>
              <li onClick={() => setNav('explore')}>
                <Link
                  to="/explore"
                  className={`text-base font-medium  hover:text-primary ${nav === 'explore' ? '' : ''} `}
                >
                  Explore
                </Link>
              </li>
              {/* <li onClick={() => setNav('contact')}>
                <Link
                  to="/contact"
                  className={`text-base font-medium  hover:text-primary ${nav === 'footer' ? '' : ''} `}
                >
                  Contact
                </Link>
              </li> */}

              <li
                onClick={() => setIsHovered(!isHovered)}
                className="relative group "
                onMouseEnter={() => setIsHovered(true)}
              >
                <Link
                  to="#"
                  className={`text-base font-medium flex  hover:text-primary ${nav === 'footer' ? '' : ''} `}
                >
                  <span>Curriculum</span>
                  <span className={`${isHovered ? 'rotate-180' : ''}   `}>
                    {' '}
                    <ChevronDown />
                  </span>
                </Link>
              </li>
            </ul>
          </div>
          <div className="flex items-center gap-4 ml-4 ">
            {/* <Link 
            // to="/login"
            to="/coming-soon"
            >
              <div>
                <Button text="SIGN UP/ LOGIN" onClick={() => {}} />
              </div>
            </Link> */}
            <span className="lg:flex lg:text-primary lg:text-base font-semibold  w-max hidden">
              <Link to={process.env.REACT_APP_TUTOR_DOMAIN || '#'} target="_blank" rel="noopener noreferrer">
                Become a tutor
              </Link>
              {/* ERP */}
            </span>
            {/* <Link to="https://worldotutor.schoolpad.in/loginManager/load"  className='lg:flex hidden'> */}
            <div className="relative lg:flex hidden group">
              <Button text="LOGIN" onClick={() => {}} />
              {/* Dropdown for login/register */}
              <div className="absolute left-0 mt-2 w-40  z-50 hidden group-hover:block bg-transparent">
                <div className=" bg-white shadow-lg border w-fite border-gray-200 mt-11 p-2 relative">
                  <div className="flex flex-col gap-0">
                    {/* <Link
                      to="https://worldotutor.schoolpad.in/loginManager/load"
                      className="block px-4 py-2 text-primary text-base font-semibold hover:bg-primary hover:text-white transition-all"
                    >
                      ERP Login
                    </Link> */}
                    <Link
                      to="https://learn.worldotutor.com/login"
                      className="block px-4 py-2 text-primary text-base font-semibold hover:bg-primary hover:text-white transition-all"
                    >
                      Teacher Login
                    </Link>
                    <Link
                      to="https://learn.worldotutor.com/login"
                      className="block px-4 py-2 text-primary text-base font-semibold hover:bg-primary hover:text-white transition-all"
                    >
                      Student Login
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            {/* </Link> */}

            {/* Booktrial */}
            <Link to="/booktrail" className="lg:flex hidden">
              <div>
                <Button text="Book a Free Trial" onClick={() => {}} />
              </div>
            </Link>

            {/* Pay Now */}

            <Link to="/paynow" className="lg:flex hidden">
              <div>
                <Button text="Pay Now" onClick={() => {}} />
              </div>
            </Link>
          </div>
        </div>
        <div className="block lg:hidden w-10 h-6 pl-auto">
          <img onClick={() => onOpenSidebar()} src={HAMBURGER} className="w-6 h-6 cursor-pointer" />
        </div>
      </div>
      {isHovered && (
        <div className="absolute top-12 group-hover:block bg-transparent  -z-10">
          <div
            className="bg-white shadow-lg border w-screen border-gray-200 mt-4 py-7 relative"
            onMouseLeave={() => setIsHovered(false)}
          >
            <div className="max-w-[90%] mx-auto relative max-h-[85vh] overflow-auto">
              {/* <ul className=''>
                  {subjects.map((subject, index) => (
                    <li key={index} className="mb-4 last:mb-0">
                      <NavItem name={subject.name} icon={subject.icon} programs={subject.programs} index={index} />
                    </li>
                  ))}
                </ul> */}
              <NavItem subjectCategory={subjectData} setIsHovered={setIsHovered} />
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

function NavItem({ subjectCategory, setIsHovered }: any) {
  const [activeSubjectIndex, setActiveSubjectIndex] = useState<any>(null);
  const [mobileActiveIndex, setMobileActiveIndex] = useState<number>(-1);
  const [activeSubjectData, setActiveSubjectData] = useState<any[]>([]);
  const handleSubjectClick = (index: number) => {
    setMobileActiveIndex(mobileActiveIndex === index ? -1 : index);
  };

  useEffect(() => {
    if (subjectCategory && subjectCategory.length > 0 && activeSubjectIndex === null) {
      setActiveSubjectIndex(subjectCategory[0]._id);
    }
  }, [subjectCategory, activeSubjectIndex]);

  useEffect(() => {
    if (activeSubjectIndex === null) {
      return;
    }
    xhr({
      url: `/s/getSubjectsByCategory/${activeSubjectIndex}`,
      method: 'GET'
    })
      .then((res) => setActiveSubjectData(res.data || []))
      .catch((err) => err);
  }, [activeSubjectIndex]);

  return (
    <>
      {/* Mobile View: Collapsible Accordion */}
      <div className="w-full lg:hidden">
        <div className="space-y-2">
          {subjectCategory.length > 0 &&
            subjectCategory.map((subject: any, idx: number) => (
              <div key={idx} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                {/* Collapsible Header */}
                <button
                  className="w-full flex items-center justify-between gap-4 p-4 text-left hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset transition-colors"
                  onClick={() => {
                    handleSubjectClick(idx);
                    setActiveSubjectIndex(subject?._id);
                  }}
                  aria-expanded={mobileActiveIndex === idx}
                  aria-controls={`mobile-subject-${idx}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-primary">
                      <Code2Icon className="w-5 h-5" />
                    </span>
                    <span className="font-medium text-sm capitalize text-gray-800">{subject?.name}</span>
                  </div>
                  <div className="flex-shrink-0">
                    <ChevronDown
                      className={`w-4 h-4 text-gray-500 transform transition-transform duration-200 ${
                        mobileActiveIndex === idx ? 'rotate-180' : ''
                      }`}
                    />
                  </div>
                </button>

                {/* Collapsible Content */}
                <div
                  id={`mobile-subject-${idx}`}
                  className={`overflow-scroll transition-all duration-300 ease-in-out ${
                    mobileActiveIndex === idx ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="p-4 bg-gray-50 border-t border-gray-200">
                    <div className="space-y-3">
                      {activeSubjectData && activeSubjectData.length > 0 ? (
                        activeSubjectData.map((option: any, pIndex: number) => (
                          <Link
                            key={pIndex}
                            reloadDocument
                            to={`/subject-details/${option?.id}`}
                            className="block w-full"
                            onClick={() => setIsHovered(false)}
                          >
                            <div className="p-3 rounded-lg border border-gray-200 bg-white hover:border-primary hover:shadow-md cursor-pointer transition-all duration-200 group">
                              <h4 className="font-semibold text-base mb-2 text-gray-800 group-hover:text-primary transition-colors">
                                {option.name}
                              </h4>
                              <div className="text-xs text-gray-500 flex items-center gap-2">
                                <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-800 text-xs font-medium">
                                  Online
                                </span>
                              </div>
                            </div>
                          </Link>
                        ))
                      ) : (
                        <div className="text-gray-500 text-sm text-center py-4">
                          No subjects available for this category.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Desktop View: Grid */}
      <div className="hidden lg:grid grid-cols-12 gap-8 w-full">
        <div className="col-span-3 rounded-md p-2">
          <ul className="fles flex-col gap-5">
            {subjectCategory.length > 0 &&
              subjectCategory.map((subject: any, idx: number) => (
                <li
                  key={idx}
                  className={`rounded-md mb-2 ${activeSubjectIndex === subject?._id ? 'bg-primary text-white shadow' : 'hover:bg-gray-100 bg-gray-100'}`}
                  onMouseEnter={() => setActiveSubjectIndex(subject?._id)}
                >
                  <h2 className="flex items-center gap-4 p-3">
                    {/* <span className="text-xl">{subject?.icon}</span> */}
                    <span className="font-medium text-sm capitalize">{subject?.name}</span>
                  </h2>
                </li>
              ))}
          </ul>
        </div>

        <div className="col-span-9 overflow-y-auto max-h-[75vh] z-10">
          {activeSubjectData.length > 0 && (
            <div className="flex flex-wrap gap-6">
              {activeSubjectData.map((option, pIndex) => (
                <Link
                  key={pIndex}
                  reloadDocument
                  to={`/subject-details/${option?.id}`}
                  className="max-w-[350px] w-full"
                  onClick={() => setIsHovered(false)}
                >
                  <div className="p-4 rounded-lg border flex flex-col justify-between h-full hover:border-gray-200 hover:shadow-sm cursor-pointer transition-all">
                    <h4 className="font-semibold text-xl mb-2 text-gray-800">{option.name}</h4>
                    <div className="text-xs text-gray-500 space-y-1 flex items-center py-2">
                      {/* <p><strong>Duration:</strong> {option.sessions}</p> */}
                      <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-800 text-xs font-medium">
                        Online
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
